import React from 'react';
import { Shield, CheckCircle, Eye, Zap } from 'lucide-react';

interface Props {
  onStart: () => void;
}

const LandingPage: React.FC<Props> = ({ onStart }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
              <Shield className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-6xl font-bold mb-4">
            <span className="gradient-text">GIA-RM Toolkit</span>
          </h1>
          <p className="text-2xl text-gray-600 mb-6">
            AI Risk Assessment Tool
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
              <span className="pulse-dot">●</span> Ready to Use
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
              <Eye className="w-4 h-4" /> Responsive Design
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
              <Zap className="w-4 h-4" /> Interactive UI
            </span>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-indigo-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Comprehensive Assessment</h3>
            <p className="text-gray-600">
              Evaluate AI systems across 6 key principles with 45+ questions
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Framework Aligned</h3>
            <p className="text-gray-600">
              Based on Canadian AI regulations (AIDA - Bill C-27)
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-pink-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Actionable Results</h3>
            <p className="text-gray-600">
              Get detailed reports with recommendations and risk scores
            </p>
          </div>
        </div>

        {/* Principles Overview */}
        <div className="bg-white p-8 rounded-2xl shadow-lg mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Assessment Principles</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <span className="text-3xl">🔍</span>
              <div>
                <div className="font-semibold">Transparency</div>
                <div className="text-sm text-gray-600">8 questions</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <span className="text-3xl">⚖️</span>
              <div>
                <div className="font-semibold">Fairness</div>
                <div className="text-sm text-gray-600">9 questions</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
              <span className="text-3xl">📋</span>
              <div>
                <div className="font-semibold">Accountability</div>
                <div className="text-sm text-gray-600">7 questions</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
              <span className="text-3xl">👤</span>
              <div>
                <div className="font-semibold">Human Oversight</div>
                <div className="text-sm text-gray-600">7 questions</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <span className="text-3xl">🛡️</span>
              <div>
                <div className="font-semibold">Safety & Security</div>
                <div className="text-sm text-gray-600">9 questions</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg">
              <span className="text-3xl">✓</span>
              <div>
                <div className="font-semibold">Validity & Robustness</div>
                <div className="text-sm text-gray-600">5 questions</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <button
            onClick={onStart}
            className="btn-primary text-lg px-12 py-4"
          >
            Start Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
